<template>
    <analytics-stat-card
        :href="props.href"
        :loading="loading"
        :error="Boolean(error)"
        class="grow"
        :value="value"
        :backgroundIcon="props.backgroundIcon"
    >
        <template v-slot:title>
            <slot name="title">{{ resource.title }}</slot>
        </template>
        <template v-slot:subtitle>
            <slot name="subtitle"></slot>
        </template>

        <span>{{ vxFormatCount(value) }}</span>

        <template v-slot:explainHeader>{{ resource.title }}</template>
        <template v-slot:explainContent>
            <p>{{ resource.explainContent }}</p>

            <router-button :to="resource.explainLink" size="sm" color="muted">
                {{ resource.explainButtonText }}
                <font-awesome-icon widthAuto :icon="faArrowRight"></font-awesome-icon>
            </router-button>
        </template>
    </analytics-stat-card>
</template>

<script setup>
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import RouterButton from '~/components/general/RouterButton.vue';
import AnalyticsStatCard from '../../partial/AnalyticsStatCard.vue';
import { vxFormatCount } from '@voxie/frontend-components';
import { outboundMessagesAnalyticsClient } from '~/services';
import { watch, computed } from 'vue';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import { metricsInputValidator } from '~/utils/analytics';
import { SENDER_TYPE_CORPORATE, SENDER_TYPE_ORG_UNIT, SENDER_TYPE_GROUP } from '~/constants/senderTypes';

const props = defineProps({
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
    resourceType: {
        type: String,
        validator: (resourceType) => ['user', 'campaign', 'automation'].includes(resourceType),
        required: true,
    },
    senderType: {
        type: String,
        validator: (value) => [SENDER_TYPE_CORPORATE, SENDER_TYPE_ORG_UNIT, SENDER_TYPE_GROUP, null].includes(value),
        default: null, // null means "use parent fetchParams as-is"
    },
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
    href: {
        type: String,
    },
    backgroundIcon: {
        type: Object,
    },
});

const teamId = Spark.state.currentTeam.id;

const { loading, response, error, fetchData } = useVersionedRequest();

const resource = computed(() => {
    switch (props.resourceType) {
        case 'user':
            return {
                title: 'Active Users',
                explainContent:
                    'Number of Message Hub agents responsible for sending messages in the selected period of time.',
                explainLink: '/message-hub',
                explainButtonText: 'Go to Message Hub',
            };
        case 'campaign':
            return {
                title: 'Active Campaigns',
                explainContent: 'Number of Campaigns that have been Live Campaigns in the selected period of time.',
                explainLink: '/messaging/campaigns',
                explainButtonText: 'Go to Campaigns',
            };
        case 'automation':
            return {
                title: 'Active Automations',
                explainContent:
                    'Number of Automations Active that have at least an Action that sends a Message in the selected period of time.',
                explainLink: '/automation/event',
                explainButtonText: 'Go to Automations',
            };
        default:
            return {
                title: 'Unknown',
                explainContent: 'unknown',
                explainLink: '/',
                explainButtonText: 'Go to',
            };
    }
});

const value = computed(() => {
    if (error.value) {
        return undefined;
    }

    if (props.resourceType === 'campaign') {
        let oneTimeCampaignValue = null;
        let evergreenCampaignValue = null;

        if (props.fetchParams.filters.resource_types?.includes('one_time_campaign')) {
            oneTimeCampaignValue =
                response.value?.data?.data?.find((result) => result.resource_type === 'one_time_campaign')?.values[0] ??
                null;
        }

        if (props.fetchParams.filters.resource_types?.includes('evergreen_campaign')) {
            evergreenCampaignValue =
                response.value?.data?.data?.find((result) => result.resource_type === 'evergreen_campaign')
                    ?.values[0] ?? null;
        }

        return oneTimeCampaignValue === null && evergreenCampaignValue === null
            ? undefined
            : (oneTimeCampaignValue || 0) + (evergreenCampaignValue || 0);
    }

    return response.value?.data?.data?.find((result) => result.resource_type === props.resourceType)?.values[0];
});

const disabled = computed(() => {
    const paramsIncludeResourceType =
        props.resourceType === 'campaign'
            ? props.fetchParams.filters.resource_types?.find((type) =>
                  ['one_time_campaign', 'evergreen_campaign'].includes(type)
              )
            : props.fetchParams.filters.resource_types?.includes(props.resourceType);

    const paramsIncludeSenderType = props.senderType
        ? (props.fetchParams.filters.sender_types ?? []).includes(props.senderType)
        : true;

    return !paramsIncludeResourceType || !paramsIncludeSenderType;
});

const params = computed(() => {
    const filters = {
        ...props.fetchParams.filters,
        sender_types: props.senderType ? [props.senderType] : props.fetchParams.filters.sender_types,
        resource_types: props.fetchParams.filters.resource_types?.filter((type) => type !== 'other'),
    };

    return {
        range: props.fetchParams.range,
        filters: filters,
    };
});

watch(
    () => props.fetchParams,
    () => {
        if (disabled.value) {
            response.value = undefined;
            return;
        }

        fetchData(
            outboundMessagesAnalyticsClient.getTotalActiveResourcesByResourceType(
                {
                    teamId,
                    orgUnitId: props.orgUnitId,
                    groupId: props.groupId,
                },
                params.value
            )
        ).catch((e) => {
            console.error(e);
        });
    },
    { immediate: true, deep: true }
);

defineExpose({
    value,
    loading,
});
</script>
